import { toast } from "sonner";
import * as Sentry from "@sentry/react";

/**
 * Shows a standardized error toast and reports the error to Sentry.
 * @param {Error} error - The error object.
 * @param {Object} [context={}] - Additional context for debugging.
 */
export const showErrorToast = (error, context = {}) => {
  const errorId =
    Sentry.captureException(error, { extra: context }) || "Unavailable";
  // Safely extract a string message — Pydantic validation errors return
  // detail as an array of objects with {type, loc, msg, input, url} keys.
  const rawDetail = error?.response?.data?.detail ?? error?.data?.detail;
  let message;
  if (Array.isArray(rawDetail)) {
    message = rawDetail.map((item) => item?.msg || JSON.stringify(item)).join(", ") || "Validation error";
  } else if (rawDetail && typeof rawDetail === "object") {
    message = rawDetail.message || rawDetail.error || rawDetail.detail || JSON.stringify(rawDetail);
  } else if (typeof rawDetail === "string") {
    message = rawDetail;
  } else {
    message = error?.message || "An unexpected error occurred.";
  }

  console.error("Toast Error:", { error, context, errorId });

  toast.error(message, {
    description: `Error ID: ${errorId}. If the problem persists, please contact support.`,
    duration: 10000,
    action: {
      label: "Copy ID",
      onClick: async () => {
        if (!navigator?.clipboard?.writeText) {
          toast.error("Clipboard unavailable.");
          return;
        }

        try {
          await navigator.clipboard.writeText(String(errorId));
          toast.success("Error ID copied to clipboard!");
        } catch {
          toast.error("Unable to copy Error ID.");
        }
      },
    },
  });
};

/**
 * Shows a success toast.
 * @param {string} message - The message to display.
 */
export const showSuccessToast = (message) => {
  toast.success(message);
};
